import { Fragment, useState } from 'react';
import { Badge, Dropdown, Spin } from 'antd';
import './index.style.less';
import { useDispatch, useSelector } from 'react-redux';
import { FaRegBell, FaTooth, FaHospital, FaFolder } from 'react-icons/fa6';
import Button from 'src/shared/components/button';
import {
  getCountNotificationsUnread,
  getCurrentNotificationsPage,
  getIsNotificationsLoading,
  getNotificationsList,
} from 'src/domain/app/redux/notifications/notification-selectors';
import { NotificationTypeValue } from 'src/shared/constants/AppEnums';
import { useNavigate } from 'react-router-dom';
import { useIntl } from 'react-intl';
import { notificationsService } from 'src/shared/services/notifications';
import MainUtils from 'src/shared/utils/main';
import {
  setCountNotificationsUnread,
  setCurrentNotificationsPage,
  setIsNotificationsLoading,
  setNotificationsList,
} from 'src/domain/app/redux/notifications/notification-slice';
import { NotificationsDto } from 'src/shared/services/notifications/dto/notification-dto';
import hasPermission from 'src/shared/functions/access-control';
import { ManageTreatments } from 'src/shared/constants/permissions-enum';
import moment from 'moment';

const TYPE_CONFIG: Record<number, { Icon: any; color: string; bg: string; label: string }> = {
  [NotificationTypeValue.TreatmentOrder]: {
    Icon: FaTooth,
    color: '#ea5383',
    bg: 'rgba(234,83,131,0.1)',
    label: 'Treatment',
  },
  [NotificationTypeValue.ClinicRequest]: {
    Icon: FaHospital,
    color: '#7c6fcd',
    bg: 'rgba(124,111,205,0.1)',
    label: 'Clinic',
  },
  [NotificationTypeValue.CaseOrder]: {
    Icon: FaFolder,
    color: '#f5a623',
    bg: 'rgba(245,166,35,0.1)',
    label: 'Case',
  },
};

const getTypeConfig = (type: number) =>
  TYPE_CONFIG[type] ?? {
    Icon: FaRegBell,
    color: '#aaa',
    bg: 'rgba(0,0,0,0.06)',
    label: 'Notification',
  };

/**
 * The refinement (or additional case) a notification is about, if any. New notifications carry
 * a flat id. Older ones only have the nested list, which reads as `$values` when pushed live
 * but as `$values.$values` once stored, because the backend serialized it twice. A list with
 * more than one entry doesn't say which order it means, so it opens the original.
 */
const getAdditionalId = (
  metadata: any,
  flatKey: "additionalTreatmentId" | "additionalCaseId",
  list: any,
): string | undefined => {
  if (typeof metadata?.[flatKey] === "string") return metadata[flatKey];
  const values = Array.isArray(list?.["$values"])
    ? list["$values"]
    : list?.["$values"]?.["$values"];
  return Array.isArray(values) && values.length === 1 ? values[0]?.id : undefined;
};

const Notifications = () => {
  const { messages } = useIntl();
  const dispatch = useDispatch();
  const notificationsList = useSelector(getNotificationsList);
  const isNotificationsLoading = useSelector(getIsNotificationsLoading);
  const countNotificationsUnread = useSelector(getCountNotificationsUnread);
  const currentNotificationsPage = useSelector(getCurrentNotificationsPage);
  const navigate = useNavigate();

  const [openNotificationsDropdown, setOpenNotificationsDropdown] = useState(false);

  const navigateToItem = (item: NotificationsDto) => {
    if (!hasPermission(ManageTreatments.Details)) return;
    switch (item.type) {
      case NotificationTypeValue.TreatmentOrder:
        if (item?.metadata?.id) {
          const additionalTreatmentId = getAdditionalId(
            item.metadata,
            "additionalTreatmentId",
            item.metadata?.Treatment?.AdditionalTreatments,
          );
          navigate(
            `/treatments/details/${item.metadata.id}${
              additionalTreatmentId ? `/${additionalTreatmentId}` : ""
            }`,
          );
        }
        break;
      case NotificationTypeValue.CaseOrder:
        if (item?.metadata?.id) {
          const additionalCaseId = getAdditionalId(
            item.metadata,
            "additionalCaseId",
            item.metadata?.Case?.AdditionalCases,
          );
          navigate(
            `/cases/details/${item.metadata.id}${
              additionalCaseId ? `/${additionalCaseId}` : ""
            }`,
          );
        }
        break;
      default:
        break;
    }
  };

  const goToNotificationDetails = async (item: NotificationsDto) => {
    if (item?.isRead) {
      navigateToItem(item);
      setOpenNotificationsDropdown(false);
    } else {
      if (!MainUtils.isEmptyValue(item?.id) && hasPermission(ManageTreatments.Details))
        try {
          const response = await notificationsService.makeIsRead(item?.id);
          const data = response?.Data;
          dispatch(
            setNotificationsList({
              ...notificationsList,
              data: notificationsList?.data?.map((n) => (n?.id === data?.id ? data : n)),
            }),
          );
          if (data) {
            navigateToItem(item);
            setOpenNotificationsDropdown(false);
          }
        } catch (error) {
          console.error(error);
        }
    }
  };

  const handleOpenNotifications = async (page?: number) => {
    try {
      dispatch(setIsNotificationsLoading(true));
      const response = await notificationsService.getAllNotifications({
        Page: !MainUtils.isEmptyValue(page) ? page : 1,
        PageSize: 4,
        Sorts: `-dateCreated`,
      });
      const data = response?.Data;
      if (!MainUtils.isEmptyValue(data)) {
        if (!MainUtils.isEmptyValue(page)) {
          dispatch(
            setNotificationsList({
              ...notificationsList,
              totalItems: data?.totalItems,
              data: notificationsList?.data?.concat(data?.data),
            }),
          );
        } else {
          dispatch(setNotificationsList(data));
        }
        dispatch(setCountNotificationsUnread(0));
      }
    } catch (error) {
      console.error(error);
    } finally {
      dispatch(setIsNotificationsLoading(false));
    }
  };

  const loadMore = async () => {
    const next = currentNotificationsPage + 1;
    await handleOpenNotifications(next);
    dispatch(setCurrentNotificationsPage(next));
  };

  const hasMore =
    notificationsList &&
    notificationsList?.totalItems > 4 &&
    notificationsList?.data?.length < notificationsList?.totalItems;

  const unreadCount = countNotificationsUnread ? Number(countNotificationsUnread) : 0;

  const panel = (
    <div className='notif-panel'>
      <div className='notif-header'>
        <span className='notif-title'>
          {messages['common.notifications']?.toString() ?? 'Notifications'}
        </span>
        {unreadCount > 0 && (
          <span className='notif-unread-pill'>{unreadCount}</span>
        )}
      </div>

      <Spin spinning={isNotificationsLoading}>
        <div className='notif-list'>
          {notificationsList?.data?.length > 0 ? (
            notificationsList.data.map((item) => {
              const { Icon, color, bg, label } = getTypeConfig(item.type);
              return (
                <div
                  key={item.id}
                  className={`notif-item${item.isRead ? '' : ' notif-item-unread'}`}
                  onClick={() => goToNotificationDetails(item)}
                >
                  <div className='notif-type-icon' style={{ background: bg }}>
                    <Icon size={16} color={color} />
                  </div>
                  <div className='notif-item-body'>
                    <div className='notif-item-type' style={{ color }}>
                      {label}
                    </div>
                    <div
                      className={`notif-item-message${item.isRead ? ' notif-item-message-read' : ''}`}
                    >
                      {item.message}
                    </div>
                    <div className='notif-item-time'>
                      {moment(new Date(item.dateCreated)).fromNow()}
                    </div>
                  </div>
                  {!item.isRead && <div className='notif-unread-dot' />}
                </div>
              );
            })
          ) : (
            <div className='notif-empty'>
              <div className='notif-empty-icon'>
                <FaRegBell size={22} />
              </div>
              <span className='notif-empty-text'>
                {messages['common.thereAreNoNewNotifications']?.toString()}
              </span>
            </div>
          )}
        </div>
      </Spin>

      {hasMore && (
        <div className='notif-footer'>
          <button className='notif-load-more' onClick={loadMore}>
            {messages['common.seeMore']?.toString()}
          </button>
        </div>
      )}
    </div>
  );

  return (
    <Fragment>
      <Dropdown
        dropdownRender={() => panel}
        trigger={['click']}
        open={openNotificationsDropdown}
        onOpenChange={async (open) => {
          setOpenNotificationsDropdown(open);
          if (open) await handleOpenNotifications();
        }}
        placement='bottomRight'
        overlayStyle={{ zIndex: 1052, minWidth: 0 }}
      >
        <Badge count={unreadCount} className='align-content-center'>
          <Button type='link' className='no-padding'>
            <FaRegBell size={20} />
          </Button>
        </Badge>
      </Dropdown>
    </Fragment>
  );
};

export default Notifications;
